<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import { StatusCodes } from "http-status-codes";
import { storeToRefs } from "pinia";
import type { ComputedRef, Ref } from "vue";
import LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationError,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useCreateFleet } from "~/modules/fleet/composables/useCreateFleet";
import type { FleetCreationPayload } from "~/modules/fleet/models/FleetCreationPayload.interface";
import type { FleetForm } from "~/modules/fleet/models/FleetForm.interface";
import { FleetType } from "~/modules/fleet/models/FleetType.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useUserSession } from "~/modules/user/stores/useUserSession";

const props = defineProps<{
  dtwinsUids?: string[];
}>();

const emit = defineEmits(["saved", "isValidateDisabled", "isSaving"]);

const { t } = useI18n();
const serialize = useSerialize();
const { userSession } = useUserSession();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { createFleet, result, error } = useCreateFleet({
  organizationCode: currentOrganization.value.code,
});

const fleetForm = reactive<FleetForm>({
  friendlyName: "",
  type: FleetType.STATIC,
  deviceUuids: [],
});
const fleetFormRef: Ref<HTMLFormElement | undefined | null> = ref();

const fleetFormRules = {
  friendlyName: [
    {
      required: true,
      message: t("fleet.form.validation.name"),
      whitespace: true,
      trigger: "blur",
    },
    {
      max: 50,
      message: t("input.error.maxLength", { maxLength: 50 }),
      whitespace: true,
      trigger: "blur",
    },
  ],
};

const initFleetFormStringified: string = serialize(fleetForm);
const isEdited: ComputedRef<boolean> = computed(() => {
  const stringifiedForm = serialize(fleetForm);
  return stringifiedForm === initFleetFormStringified;
});
watch(
  () => isEdited.value,
  () => {
    emit("isValidateDisabled", isEdited.value);
  },
);

async function isFormValid() {
  return await fleetFormRef.value?.validate().catch(() => false);
}

async function onSubmit() {
  if ((await isFormValid()) && userSession) {
    emit("isValidateDisabled", true);
    emit("isSaving", true);
    if (props.dtwinsUids) {
      fleetForm.deviceUuids = props.dtwinsUids;
    }
    const newFleet: FleetCreationPayload = {
      organizationCode: currentOrganization.value.code,
      friendlyName: fleetForm.friendlyName,
      type: fleetForm.type,
      devices: fleetForm.deviceUuids,
    };

    await createFleet(newFleet);

    if (LxcError.check(error.value)) {
      switch (error.value.status) {
        case StatusCodes.CONFLICT:
          showNotificationError(
            t(NotificationKey.saveError),
            t("fleet.form.validation.uniqueName"),
          );
          break;
        default:
          error.value.notify(NotificationKey.saveError);
          break;
      }
    } else {
      if (fleetForm.deviceUuids) {
        showNotificationSuccess(
          t("fleet.form.saveSuccess", {
            fleetName: result.value?.friendlyName,
          }),
        );
      } else {
        showNotificationSuccess(t(NotificationKey.saveSuccess));
      }
      onClose();
      emit("saved");
    }
  }
  emit("isValidateDisabled", false);
  emit("isSaving", false);
}

function onClose() {
  setTimeout(fleetFormRef.value?.clearValidate, 0);
  fleetForm.friendlyName = "";
}

defineExpose({
  onSubmit,
  onClose,
});

onMounted(() => {
  emit("isValidateDisabled", isEdited);
});
</script>
<template>
  <lxc-form
    ref="fleetFormRef"
    :model="fleetForm"
    :rules="fleetFormRules"
    @submit.prevent
  >
    <lxc-form-item :label="t('fleet.form.name')" prop="friendlyName">
      <lxc-input v-model="fleetForm.friendlyName" type="text" />
    </lxc-form-item>
  </lxc-form>
</template>
